/**
 * Type compatibility rules for port connections.
 *
 * Determines whether an output port can connect to an input port,
 * and what coercion (if any) is needed.
 */

import type { PortDataType, PortDefinition } from '../types';
import { IMPLICIT_COERCIONS, NUMERIC_TYPES, NUMERIC_RANK } from '../types';

// ============================================================================
// Connection result
// ============================================================================

export type ConnectionCheck =
  | { readonly ok: true; readonly coercion: PortDataType | null }
  | { readonly ok: false; readonly reason: string };

// ============================================================================
// Core checks
// ============================================================================

/**
 * Can `from` (output) connect to `to` (input)?
 *
 * Rules:
 * 1. Flow ↔ Flow only (no data)
 * 2. Same type → always ok
 * 3. Any on either side → ok
 * 4. numeric ↔ any concrete numeric type → ok
 * 5. Implicit numeric coercion (i32 → f32 → f64)
 * 6. Anything → string (toString)
 */
export function canConnect(from: PortDefinition, to: PortDefinition): ConnectionCheck {
  // Direction check
  if (from.direction !== 'output' || to.direction !== 'input') {
    return { ok: false, reason: 'Can only connect output → input' };
  }

  const ft = from.type;
  const tt = to.type;

  // Flow must match flow
  if (ft === 'flow' || tt === 'flow') {
    if (ft === 'flow' && tt === 'flow') return { ok: true, coercion: null };
    return { ok: false, reason: 'Cannot mix flow and data ports' };
  }

  // Exact match
  if (ft === tt) return { ok: true, coercion: null };

  // "any" wildcard
  if (ft === 'any' || tt === 'any') return { ok: true, coercion: null };

  // numeric ↔ concrete numeric: always compatible
  if (ft === 'numeric' && NUMERIC_TYPES.has(tt)) return { ok: true, coercion: null };
  if (tt === 'numeric' && NUMERIC_TYPES.has(ft)) return { ok: true, coercion: null };

  // Implicit coercion
  const allowed = IMPLICIT_COERCIONS.get(tt);
  if (allowed && allowed.has(ft)) {
    return { ok: true, coercion: tt };
  }

  // Object reference family: entity, gameobject, ref:* are all interchangeable
  const OBJ_REF_TYPES: ReadonlySet<string> = new Set(['entity', 'gameobject']);
  const isFromObjRef = OBJ_REF_TYPES.has(ft) || ft.startsWith('ref:');
  const isToObjRef   = OBJ_REF_TYPES.has(tt) || tt.startsWith('ref:');
  if (isFromObjRef && isToObjRef) {
    return { ok: true, coercion: tt };
  }

  return { ok: false, reason: `Cannot connect '${ft}' to '${tt}'` };
}

/**
 * Resolve the concrete numeric type for a node with `numeric` ports,
 * based on the types of incoming connections.
 *
 * Returns the widest numeric type among all connected inputs,
 * or 'f32' if nothing is connected.
 */
export function resolveNumericType(connectedInputTypes: readonly PortDataType[]): PortDataType {
  let maxRank = -1;
  let resolved: PortDataType = 'f32'; // default when unconnected

  for (const t of connectedInputTypes) {
    const rank = NUMERIC_RANK.get(t);
    if (rank !== undefined && rank > maxRank) {
      maxRank = rank;
      resolved = t === 'numeric' ? 'f32' : t; // don't resolve numeric→numeric
    }
  }

  return resolved;
}

/**
 * Check if a type is a flow type.
 */
export function isFlowType(type: PortDataType): boolean {
  return type === 'flow';
}

/**
 * Check if a type is numeric.
 */
export function isNumericType(type: PortDataType): boolean {
  return NUMERIC_TYPES.has(type);
}

// ============================================================================
// Default values per type
// ============================================================================

const DEFAULTS: Record<string, unknown> = {
  'flow':    undefined,
  'bool':    false,
  'i32':     0,
  'f32':     0.0,
  'f64':     0.0,
  'numeric': 0.0,
  'string':  '',
  'vec2':    { x: 0, y: 0 },
  'vec3':    { x: 0, y: 0, z: 0 },
  'vec4':    { x: 0, y: 0, z: 0, w: 0 },
  'quat':    { x: 0, y: 0, z: 0, w: 1 },
  'color':   0xFF_FF_FF_FF,  // white, ABGR packed
  'entity':     -1,
  'gameobject': -1,
  'any':        null,
};

/**
 * Get the default value for a data type.
 */
export function defaultValueFor(type: PortDataType): unknown {
  if (type.startsWith('ref:')) return -1;
  return DEFAULTS[type] ?? null;
}

// ============================================================================
// Generic literal formatting (for IR)
// ============================================================================

/**
 * Convert a JS runtime value to a generic IR literal string.
 * This is target-agnostic — backend code generators interpret these.
 */
export function toLiteralString(type: PortDataType, value: unknown): string {
  switch (type) {
    case 'bool':
      return value ? 'true' : 'false';
    case 'i32':
      return `${Math.trunc(value as number)}`;
    case 'f32':
    case 'f64': {
      const n = value as number;
      return Number.isInteger(n) ? `${n}.0` : `${n}`;
    }
    case 'string':
      return JSON.stringify(value);
    case 'vec2': {
      const v = value as { x: number; y: number };
      return `vec2(${v.x}, ${v.y})`;
    }
    case 'vec3': {
      const v = value as { x: number; y: number; z: number };
      return `vec3(${v.x}, ${v.y}, ${v.z})`;
    }
    case 'vec4': {
      const v = value as { x: number; y: number; z: number; w: number };
      return `vec4(${v.x}, ${v.y}, ${v.z}, ${v.w})`;
    }
    case 'quat': {
      const v = value as { x: number; y: number; z: number; w: number };
      return `quat(${v.x}, ${v.y}, ${v.z}, ${v.w})`;
    }
    case 'color':
      return `0x${((value as number) >>> 0).toString(16).padStart(8, '0').toUpperCase()}`;
    case 'entity':
    case 'gameobject':
      return `${Math.trunc(value as number)}`;
    default:
      // ref:* component reference types are entity IDs
      if (type.startsWith('ref:')) return `${Math.trunc(value as number)}`;
      return `${value}`;
  }
}

/**
 * Get a generic default literal string for a data type.
 */
export function defaultLiteralFor(type: PortDataType): string {
  switch (type) {
    case 'bool':   return 'false';
    case 'i32':    return '0';
    case 'f32':    return '0.0';
    case 'f64':    return '0.0';
    case 'string': return '""';
    case 'vec2':   return 'vec2(0, 0)';
    case 'vec3':   return 'vec3(0, 0, 0)';
    case 'vec4':   return 'vec4(0, 0, 0, 0)';
    case 'quat':   return 'quat(0, 0, 0, 1)';
    case 'color':  return '0xFFFFFFFF';
    case 'entity':
    case 'gameobject':
      return '-1';
    default:
      if (type.startsWith('ref:')) return '-1';
      return '0';
  }
}
